import { Button, Card, Center, Loader } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconEdit, IconTrash } from '@tabler/icons-react';
import PageContent from '../../../../components/core/PageContent/PageContent';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import { Permission } from '../../../../models/core/departments.type';
import { TreatmentType } from '../../../../models/core/treatments.type';
import treatmentsTreatment from '../../../../services/core/treatments.service';
import { Feature } from '../../../../utils/constants.utils';
import DataView from './components/DataView';

type Result = {
  loading: boolean;
  data: TreatmentType | null;
};

function TreatmentView() {
  const navigate = useNavigate();
  const location: any = useLocation();
  const modals = useModals();
  const { idResiduoTratamento } = useParams();

  const [result, setResult] = useState<Result>({
    loading: location?.state?.treatment === undefined,
    data: location?.state?.treatment || null,
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let data: TreatmentType | null = null;
      try {
        data = await treatmentsTreatment.select({
          idResiduoTratamento: Number(idResiduoTratamento),
        });
      } catch (error: any) {
        showNotification({
          title: 'Tratamento - Visualizar',
          message: error?.isBusinessException ? error.description : `Não foi possível carregar o tratamento.`,
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setResult({ loading: false, data });
      }
    };
    if (result.data === null) {
      fetchData();
    }
  }, [idResiduoTratamento, navigate, result]);

  const handleAction = async () => {
    try {
      setResult({ loading: false, data: result.data });

      setDeleting(true);
      await treatmentsTreatment.delete({ idResiduoTratamento: Number(idResiduoTratamento) });
      navigate('/treatments');

      showNotification({
        title: 'Tratamento - Visualizar',
        message: `Tratamento excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Tratamento - Visualizar',
        message: error?.isBusinessException ? error.description : `Não foi possível excluir o tratamento.`,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const showModal = () =>
    modals.openConfirmModal({
      title: `Gostaria de excluir esse tratamento?`,
      size: 'sm',
      closeOnClickOutside: false,
      closeOnEscape: false,
      onConfirm: () => {
        handleAction();
      },
    });

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Treatment}
        title="Tratamento - Visualizar"
        description="Visualize os detalhes de um tratamento."
        buttons={[
          <Button
            key="Voltar"
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={deleting || result.loading}
          >
            Voltar
          </Button>,
          <Button
            key="Excluir"
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              showModal();
            }}
            disabled={deleting || result.loading}
            loading={deleting}
            data-permission={Permission.TreatmentDelete}
          >
            Excluir
          </Button>,
          <Button
            key="Editar"
            color="accent"
            leftSection={<IconEdit size={18} />}
            onClick={() => {
              navigate(`/treatments/${result?.data?.idResiduoTratamento}/edit`, {
                state: {
                  treatment: result.data,
                },
              });
            }}
            disabled={deleting || result.loading}
            data-permission={Permission.TreatmentEdit}
          >
            Editar
          </Button>,
        ]}
      />
      {result.data === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <DataView data={result.data} />
        </PageContent>
      )}
    </Card>
  );
}

export default TreatmentView;
